import React, { useState, useCallback } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from './firebase';
import './Marketplace.css';

// Item categories
const CATEGORIES = [
  { id: 'dresses', label: 'Dresses', icon: '👗' },
  { id: 'shoes', label: 'Shoes', icon: '👠' },
  { id: 'costumes', label: 'Costumes', icon: '🎭' },
  { id: 'accessories', label: 'Accessories', icon: '💎' },
  { id: 'practice-wear', label: 'Practice Wear', icon: '🩱' },
  { id: 'jewelry', label: 'Jewelry', icon: '📿' },
  { id: 'other', label: 'Other', icon: '📦' }
];

// Conditions
const CONDITIONS = [
  { id: 'new', label: 'New with tags' },
  { id: 'like-new', label: 'Like New' },
  { id: 'excellent', label: 'Excellent' },
  { id: 'good', label: 'Good' },
  { id: 'fair', label: 'Fair' }
];

// Dance styles
const DANCE_STYLES = [
  'Latin', 'Standard', 'Ballroom', 'Contemporary', 'Hip Hop', 'Commercial',
  'Ballet', 'Jazz', 'Salsa', 'Tango', 'Other'
];

// Sizes
const SIZES = {
  dresses: ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'Custom'],
  shoes: ['34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45'],
  general: ['One Size', 'XS', 'S', 'M', 'L', 'XL', 'XXL']
};

export default function MarketplaceItemForm({ onSuccess, onCancel, sellerName, isAdmin }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    condition: '',
    danceStyle: '',
    size: '',
    color: '',
    brand: '',
    listingType: 'sale', // 'sale', 'rent', or 'both'
    salePrice: '',
    rentPricePerDay: '',
    minRentalDays: '1',
    maxRentalDays: '14',
    deposit: '',
    location: '',
    contactPreference: 'message', // 'message', 'phone', or 'both'
    phone: '',
    additionalInfo: ''
  });
  
  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Limit to 6 images
    if (images.length + files.length > 6) {
      setError('Maximum 6 images allowed');
      return;
    }

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed');
        return false;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Images must be under 5MB');
        return false;
      }
      return true;
    });

    // Create preview URLs
    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setImages(prev => [...prev, ...newImages]);
    setImageFiles(prev => [...prev, ...validFiles]);
    setError('');
  };

  const removeImage = (index) => {
    setImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
    setImageFiles(prev => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const uploadImages = async () => {
    const uploadedUrls = [];
    const user = auth.currentUser;
    
    if (!user) throw new Error('User not authenticated');

    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      const fileName = `${Date.now()}_${i}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, `marketplace/${user.uid}/${fileName}`);
      
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = ((i / imageFiles.length) + (snapshot.bytesTransferred / snapshot.totalBytes / imageFiles.length)) * 100;
            setUploadProgress(Math.round(progress));
          },
          (error) => reject(error),
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            uploadedUrls.push(downloadURL);
            resolve();
          }
        );
      });
    }
    
    return uploadedUrls;
  };

  const validateForm = () => {
    if (!formData.title.trim()) return 'Title is required';
    if (!formData.description.trim()) return 'Description is required';
    if (!formData.category) return 'Category is required';
    if (!formData.condition) return 'Condition is required';
    
    if (formData.listingType === 'sale' || formData.listingType === 'both') {
      if (!formData.salePrice || Number(formData.salePrice) <= 0) {
        return 'Valid sale price is required';
      }
    }
    
    if (formData.listingType === 'rent' || formData.listingType === 'both') {
      if (!formData.rentPricePerDay || Number(formData.rentPricePerDay) <= 0) {
        return 'Valid rental price per day is required';
      }
    }
    
    if (images.length === 0) return 'At least one image is required';
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError('');

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Please sign in to list an item');

      // Upload images
      setUploadProgress(0);
      const imageUrls = await uploadImages();

      // Create item document
      const itemData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        condition: formData.condition,
        danceStyle: formData.danceStyle || null,
        size: formData.size || null,
        color: formData.color || null,
        brand: formData.brand || null,
        listingType: formData.listingType,
        salePrice: formData.listingType !== 'rent' ? Number(formData.salePrice) : null,
        rentPricePerDay: formData.listingType !== 'sale' ? Number(formData.rentPricePerDay) : null,
        minRentalDays: formData.listingType !== 'sale' ? Number(formData.minRentalDays) : null,
        maxRentalDays: formData.listingType !== 'sale' ? Number(formData.maxRentalDays) : null,
        deposit: formData.deposit ? Number(formData.deposit) : null,
        location: formData.location || null,
        contactPreference: formData.contactPreference,
        phone: formData.contactPreference !== 'message' ? formData.phone : null,
        additionalInfo: formData.additionalInfo || null,
        images: imageUrls,
        sellerId: user.uid,
        sellerEmail: user.email,
        sellerName: sellerName || user.displayName || 'Anonymous',
        isVerifiedSeller: !isAdmin, // If not admin, they must be verified to access this form
        status: isAdmin ? 'approved' : 'pending', // Auto-approve admin listings
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        ...(isAdmin && { approvedAt: Timestamp.now(), approvedBy: user.uid }),
        // Rental tracking
        rentalBookings: [],
        views: 0,
        favorites: 0
      };

      await addDoc(collection(db, 'marketplaceItems'), itemData);
      
      setSuccess(true);
      
      // Clean up preview URLs
      images.forEach(img => URL.revokeObjectURL(img.preview));
      
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1500);
      
    } catch (err) {
      console.error('Error creating listing:', err);
      setError(err.message || 'Failed to create listing. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getSizeOptions = () => {
    if (formData.category === 'dresses' || formData.category === 'costumes' || formData.category === 'practice-wear') {
      return SIZES.dresses;
    }
    if (formData.category === 'shoes') {
      return SIZES.shoes;
    }
    return SIZES.general;
  };

  if (success) {
    return (
      <div className="marketplace-alert success" style={{ margin: '2rem' }}>
        <span className="alert-icon">✅</span>
        <div className="alert-content">
          <h4>Listing Created Successfully!</h4>
          <p>
            {isAdmin 
              ? 'Your item is now live on the marketplace.'
              : 'Your item has been submitted for review. You will be notified once approved.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form className="marketplace-form" onSubmit={handleSubmit}>
      {error && (
        <div className="marketplace-alert error">
          <span className="alert-icon">⚠️</span>
          <div className="alert-content">
            <p>{error}</p>
          </div>
        </div>
      )}

      {!isAdmin && (
        <div className="marketplace-alert info">
          <span className="alert-icon">ℹ️</span>
          <div className="alert-content">
            <h4>Review Required</h4>
            <p>Your listing will be reviewed by an admin before appearing on the marketplace.</p>
          </div>
        </div>
      )}

      {/* Image Upload Section */}
      <div className="image-upload-section">
        <label>Photos <span className="required">*</span></label>
        <p className="form-hint">Add up to 6 photos. First photo will be the main image.</p>
        
        <div className="image-upload-grid">
          {images.map((img, index) => (
            <div key={index} className="image-upload-slot">
              <img src={img.preview} alt={`Preview ${index + 1}`} className="image-preview" />
              <button 
                type="button" 
                className="remove-image-btn"
                onClick={() => removeImage(index)}
              >
                ×
              </button>
            </div>
          ))}
          
          {images.length < 6 && (
            <div className="image-upload-slot">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                multiple
              />
              <div className="upload-placeholder">
                <span className="icon">📷</span>
                <span>Add Photo</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Basic Info */}
      <div className="form-group full-width">
        <label>Title <span className="required">*</span></label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder="e.g., Stunning Red Latin Dress with Crystals"
          maxLength={100}
        />
      </div>

      <div className="form-group full-width">
        <label>Description <span className="required">*</span></label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Describe your item in detail. Include measurements, any defects, and why you're selling/renting..."
          maxLength={2000}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Category <span className="required">*</span></label>
          <select name="category" value={formData.category} onChange={handleInputChange}>
            <option value="">Select Category</option>
            {CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Condition <span className="required">*</span></label>
          <div className="condition-selector">
            {CONDITIONS.map(cond => (
              <button
                key={cond.id}
                type="button"
                className={`condition-option ${formData.condition === cond.id ? 'selected' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, condition: cond.id }))}
              >
                {cond.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Dance Style</label>
          <select name="danceStyle" value={formData.danceStyle} onChange={handleInputChange}>
            <option value="">Select Style (Optional)</option>
            {DANCE_STYLES.map(style => (
              <option key={style} value={style}>{style}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Size</label>
          <select name="size" value={formData.size} onChange={handleInputChange}>
            <option value="">Select Size (Optional)</option>
            {getSizeOptions().map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Color</label>
          <input
            type="text"
            name="color"
            value={formData.color}
            onChange={handleInputChange}
            placeholder="e.g., Red, Blue, Multi-color"
          />
        </div>

        <div className="form-group">
          <label>Brand</label>
          <input
            type="text"
            name="brand"
            value={formData.brand}
            onChange={handleInputChange}
            placeholder="e.g., Chrisanne, DSI London"
          />
        </div>
      </div>

      {/* Listing Type */}
      <div className="form-group full-width">
        <label>Listing Type <span className="required">*</span></label>
        <div className="listing-type-toggle">
          <label className={`toggle-option ${formData.listingType === 'sale' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="listingType"
              value="sale"
              checked={formData.listingType === 'sale'}
              onChange={handleInputChange}
            />
            <span className="toggle-icon">💰</span>
            <span className="toggle-label">For Sale</span>
            <span className="toggle-desc">Sell your item</span>
          </label>
          
          <label className={`toggle-option ${formData.listingType === 'rent' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="listingType"
              value="rent"
              checked={formData.listingType === 'rent'}
              onChange={handleInputChange}
            />
            <span className="toggle-icon">📅</span>
            <span className="toggle-label">For Rent</span>
            <span className="toggle-desc">Rent out your item</span>
          </label>
          
          <label className={`toggle-option ${formData.listingType === 'both' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="listingType"
              value="both"
              checked={formData.listingType === 'both'}
              onChange={handleInputChange}
            />
            <span className="toggle-icon">✨</span>
            <span className="toggle-label">Both</span>
            <span className="toggle-desc">Sell or rent</span>
          </label>
        </div>
      </div>

      {/* Pricing */}
      {(formData.listingType === 'sale' || formData.listingType === 'both') && (
        <div className="form-row">
          <div className="form-group">
            <label>Sale Price (€) <span className="required">*</span></label>
            <input
              type="number"
              name="salePrice"
              value={formData.salePrice}
              onChange={handleInputChange}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>
        </div>
      )}

      {(formData.listingType === 'rent' || formData.listingType === 'both') && (
        <>
          <div className="form-row">
            <div className="form-group">
              <label>Rental Price per Day (€) <span className="required">*</span></label>
              <input
                type="number"
                name="rentPricePerDay"
                value={formData.rentPricePerDay}
                onChange={handleInputChange}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            
            <div className="form-group">
              <label>Security Deposit (€)</label>
              <input
                type="number"
                name="deposit"
                value={formData.deposit}
                onChange={handleInputChange}
                placeholder="Optional"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Minimum Rental Days</label>
              <input
                type="number"
                name="minRentalDays"
                value={formData.minRentalDays}
                onChange={handleInputChange}
                min="1"
              />
            </div>
            
            <div className="form-group">
              <label>Maximum Rental Days</label>
              <input
                type="number"
                name="maxRentalDays"
                value={formData.maxRentalDays}
                onChange={handleInputChange}
                min="1"
              />
            </div>
          </div>
        </>
      )}

      {/* Contact Info */}
      <div className="form-row">
        <div className="form-group">
          <label>Location</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            placeholder="e.g., Athens, Thessaloniki"
          />
        </div>

        <div className="form-group">
          <label>Contact Preference</label>
          <select name="contactPreference" value={formData.contactPreference} onChange={handleInputChange}>
            <option value="message">In-app Message Only</option>
            <option value="phone">Phone Only</option>
            <option value="both">Both Message & Phone</option>
          </select>
        </div>
      </div>

      {(formData.contactPreference === 'phone' || formData.contactPreference === 'both') && (
        <div className="form-group">
          <label>Phone Number</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="+30 xxx xxx xxxx"
          />
        </div>
      )}

      <div className="form-group full-width">
        <label>Additional Information</label>
        <textarea
          name="additionalInfo"
          value={formData.additionalInfo}
          onChange={handleInputChange}
          placeholder="Any other details buyers/renters should know..."
          rows={3}
        />
      </div>

      {/* Submit */}
      <div className="form-submit-section">
        <button 
          type="button" 
          className="submit-btn secondary"
          onClick={onCancel}
          disabled={uploading}
        >
          Cancel
        </button>
        
        <button 
          type="submit" 
          className="submit-btn primary"
          disabled={uploading}
        >
          {uploading ? (
            <>
              <span>Uploading... {uploadProgress}%</span>
            </>
          ) : (
            <>
              <span>📤</span>
              <span>{isAdmin ? 'Publish Listing' : 'Submit for Review'}</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}
